import { useTranslation } from 'react-i18next';
import { Receipt } from '@parrot-software/design-system/icons';
import type { SubscriptionBlockViewDto } from '@subscriptions/application/dtos';
import { AdvanceInvoiceBadge, InfoItem, MissingDataBadge } from '@subscriptions/presentation/components/base';
import { displayValue, getInvoiceDisplayName } from '@subscriptions/presentation/utils';

interface SubscriptionInvoiceInfoProps {
  advanceInvoice: boolean;
  invoiceData: SubscriptionBlockViewDto['invoiceData'];
}

/**
 * SubscriptionInvoiceInfo - "Datos fiscales" column of the subscription block card.
 *
 * Shows the taxpayer name — `businessName` for persona moral, the person's full name
 * for persona física — and the RFC of the block's invoice data, or the missing-data
 * badge when the block has none. When `advanceInvoice` is `true`, an info
 * "Factura anticipada" badge renders inline next to the column label.
 */
export const SubscriptionInvoiceInfo = ({ advanceInvoice, invoiceData }: SubscriptionInvoiceInfoProps) => {
  const { t } = useTranslation('subscriptions');

  const badge = advanceInvoice ? <AdvanceInvoiceBadge /> : undefined;
  const icon = <Receipt className="h-4 w-4" />;

  if (!invoiceData) {
    return (
      <InfoItem badge={badge} icon={icon} label={t('invoice.label')}>
        <MissingDataBadge label={t('invoice.noData')} />
      </InfoItem>
    );
  }

  return (
    <InfoItem badge={badge} icon={icon} label={t('invoice.label')}>
      <p className="text-sm font-semibold leading-5 text-foreground break-words">
        {displayValue(getInvoiceDisplayName(invoiceData))}
      </p>
      <p className="text-sm font-medium leading-5 text-foreground break-words">
        {t('invoice.rfc', { rfc: displayValue(invoiceData.rfc) })}
      </p>
    </InfoItem>
  );
};
